import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Link,
  Button,
  styled,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";

// 1) A custom gradient button
const GradientButton = styled(Button)(() => ({
  fontFamily: '"Lexend", sans-serif',
  background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
  color: "#fff",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: 8,
  padding: "14px",
  width: "100%",
  fontSize: "1rem",
  "&:hover": {
    background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
    boxShadow: "0 4px 14px rgba(99,102,241,0.3)",
  },
}));

export default function PasswordResetPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Manage show/hide states for each password field
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Example local state for new password, confirm
  // (In real usage, handle with Formik or an API)
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleReset = () => {
    // e.g. Submit new password to your backend
    alert(`Password: ${password}\nConfirm: ${confirmPassword}`);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#f8f8f8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: isMobile ? "90%" : 500,
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
        }}
      >
        {/* StageLink Logo */}
        <Typography
          variant="h4"
          sx={{
            mb: 1,
            letterSpacing: 1,
            fontFamily: '"Lexend", sans-serif',
            fontWeight: 700,
          }}
        >
          StageLink
        </Typography>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            fontFamily: '"Lexend", sans-serif',
            fontWeight: 700,
          }}
        >
          Reset password
        </Typography>

        {/* Subheading */}
        <Typography
          variant="body1"
          sx={{
            mb: 3,
            fontFamily: '"Lexend", sans-serif',
            color: "#666",
          }}
        >
          Please type something you’ll remember
        </Typography>

        {/* Password Field */}
        <TextField
          variant="outlined"
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ color: "#999" }}>
                <LockIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Confirm Password Field */}
        <TextField
          variant="outlined"
          label="Confirm Password"
          type={showConfirm ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ color: "#999" }}>
                <LockIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Gradient Reset Button */}
        <GradientButton onClick={handleReset}>
          Reset password
        </GradientButton>

        {/* "Remember password?" link */}
        <Typography
          variant="body2"
          sx={{ mt: 3, fontFamily: '"Lexend", sans-serif' }}
        >
          Remember password?{" "}
          <Link
            href="/sign-in"
            underline="none"
            sx={{ fontWeight: 600, fontFamily: '"Lexend", sans-serif' }}
          >
            Log in
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
}
